// ABOUTME: Dedicated page for viewing and editing all per-child settings
// ABOUTME: Replaces the EditChildModal with a full tabbed page at /family/child/:childId

import { useState, useEffect, type ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Tabs, Tab, Avatar, Paper, IconButton, Tooltip,
  CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { familyAPI } from '../services/api';
import { Child } from '../types/api';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarUrl } from '../utils/avatarUtils';
import SharedAppBar from './SharedAppBar';
import naluV1Theme from '../theme/theme.nalu-v1';
import ProfileTab from './ChildProfilePage/ProfileTab';
import MakeItRightTab from './ChildProfilePage/MakeItRightTab';
import LetsResetTab from './ChildProfilePage/LetsResetTab';
import OneOnOneTimeTab from './ChildProfilePage/OneOnOneTimeTab';
import GoalsTab from './ChildProfilePage/GoalsTab';
import ChoresTab from './ChildProfilePage/ChoresTab';
import CalendarsTab from './ChildProfilePage/CalendarsTab';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`child-profile-tabpanel-${index}`}
      aria-labelledby={`child-profile-tab-${index}`}
    >
      <Box sx={{ pt: 3 }}>{children}</Box>
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `child-profile-tab-${index}`,
    'aria-controls': `child-profile-tabpanel-${index}`,
  };
}

export default function ChildProfilePage() {
  const { childId: childIdParam } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  // Compared by value (logoSrc), not object identity — dev-server HMR can give the
  // ThemeProvider's resolved theme and this module's naluV1Theme import different
  // object references after a hot-reload even though both are "the nalu-v1 theme".
  const isNaluV1Theme = theme.logoSrc === naluV1Theme.logoSrc;
  const [child, setChild] = useState<Child | null>(null);
  const [familyId, setFamilyId] = useState<number>(0);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false);

  const isAdmin = user?.isAdmin ?? false;
  const isCreateMode = childIdParam === 'new';
  const isUserFamilyOwner = isAdmin || user?.role === 'Owner';

  const tabLabels = [
    'Profile',
    'Make It Right',
    'Reset',
    'Let Go',
    'Goals',
    'Responsibilities',
    ...(isAdmin ? ['Calendars'] : []),
  ];

  useEffect(() => {
    if (isCreateMode) {
      loadFamilyIdForCreate();
      return;
    }
    loadChild();
  }, [childIdParam]); // eslint-disable-line react-hooks/exhaustive-deps -- loadChild/loadFamilyIdForCreate are stable internal functions

  async function loadFamilyIdForCreate() {
    try {
      setLoading(true);
      const families = await familyAPI.getFamilies();
      if (families.length > 0) setFamilyId(families[0].familyId);
    } finally {
      setLoading(false);
    }
  }

  async function loadChild() {
    try {
      setLoading(true);
      const families = await familyAPI.getFamilies();
      if (families.length === 0) return;
      const fid = families[0].familyId;
      setFamilyId(fid);
      const children = await familyAPI.getChildren(fid);
      const childIdNum = parseInt(childIdParam ?? '', 10);
      if (isNaN(childIdNum)) return;
      const found = children.find(
        (c) => (c.childId ?? (c as { id?: number }).id) === childIdNum
      );
      if (found) setChild(found);
    } finally {
      setLoading(false);
    }
  }

  function handleBack() {
    if (isCreateMode) {
      setDiscardDialogOpen(true);
    } else {
      navigate('/family');
    }
  }

  function handleDiscardConfirm() {
    setDiscardDialogOpen(false);
    navigate('/family');
  }

  function handleChildCreated(newChildId: number) {
    navigate(`/family/child/${newChildId}`, { replace: true });
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <SharedAppBar
          title="Child Profile"
          showBackButton
          onBackClick={handleBack}
        />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <SharedAppBar
        title="Child Profile"
        showBackButton
        onBackClick={handleBack}
      />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Read-only header — nalu-v1's "identity card" gets a distinct sea-teal tint
            (approximated from theme.palette.info.main, the closest palette color to the
            mockup's off-palette #DFEEE7/#9BD0C2) so it reads as the page's header, not
            just another flat card (issue #1900). */}
        <Paper
          sx={{
            p: 3,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            ...(isNaluV1Theme && {
              bgcolor: alpha(theme.palette.info.main, 0.25),
              border: `1px solid ${theme.palette.info.main}`,
            }),
          }}
        >
          <Tooltip title="Back to My Family">
            <IconButton onClick={handleBack} aria-label="back to my family">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Avatar
            src={child?.profileImageUrl ? getAvatarUrl(child.profileImageUrl) : undefined}
            sx={{
              width: 64,
              height: 64,
              ...(isNaluV1Theme && {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }),
            }}
          >
            {child?.name?.[0]?.toUpperCase()}
          </Avatar>
          <Box>
            <Typography
              variant="h5"
              sx={isNaluV1Theme ? {
                fontFamily: theme.typography.h3.fontFamily,
                fontSize: '26px',
                fontWeight: 600,
                lineHeight: 1.1,
              } : undefined}
            >
              {isCreateMode ? 'New Child' : (child?.name ?? '...')}
            </Typography>
            {!isCreateMode && child?.age != null && (
              <Typography variant="body2" color="text.secondary">
                Age {child.age}
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Tabs — nalu-v1's MuiTabs/MuiTab overrides already render these as a pill nav
            with no underline indicator, so drop the divider border for that theme
            (issue #1900). */}
        <Box sx={{ borderBottom: isNaluV1Theme ? 0 : 1, borderColor: 'divider', mb: isNaluV1Theme ? 3 : 0 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            aria-label="child profile tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabLabels.map((label, i) => (
              <Tab key={label} label={label} {...a11yProps(i)} />
            ))}
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          {child && (
            <ProfileTab
              child={child}
              onChildUpdated={(updated) => setChild((c) => c ? { ...c, ...updated } : c)}
              isUserFamilyOwner={isUserFamilyOwner}
            />
          )}
          {isCreateMode && (
            <ProfileTab
              child={{ childId: 0, name: '', birthYear: 0, createdAt: '', aliases: [] }}
              onChildUpdated={() => {}}
              isUserFamilyOwner={isUserFamilyOwner}
              isCreateMode
              familyId={familyId}
              onChildCreated={handleChildCreated}
            />
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          {child && <MakeItRightTab childId={child.childId!} />}
          {isCreateMode && <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Save the Profile tab first to use this tab.</Typography>}
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          {child && <LetsResetTab childId={child.childId!} />}
          {isCreateMode && <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Save the Profile tab first to use this tab.</Typography>}
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          {child && <OneOnOneTimeTab childId={child.childId!} />}
          {isCreateMode && <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Save the Profile tab first to use this tab.</Typography>}
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          {child && <GoalsTab child={child} familyId={familyId} />}
          {isCreateMode && <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Save the Profile tab first to use this tab.</Typography>}
        </TabPanel>
        <TabPanel value={activeTab} index={5}>
          {child && <ChoresTab child={child} familyId={familyId} />}
          {isCreateMode && <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Save the Profile tab first to use this tab.</Typography>}
        </TabPanel>
        {isAdmin && (
          <TabPanel value={activeTab} index={6}>
            {child && <CalendarsTab childId={child.childId!} familyId={familyId} />}
            {isCreateMode && <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>Save the Profile tab first to use this tab.</Typography>}
          </TabPanel>
        )}
      </Container>

      {/* Discard confirmation for create mode */}
      <Dialog open={discardDialogOpen} onClose={() => setDiscardDialogOpen(false)}>
        <DialogTitle>Discard new child?</DialogTitle>
        <DialogContent>
          <Typography>Your new child hasn&apos;t been saved. Go back to My Family?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardDialogOpen(false)}>Keep editing</Button>
          <Button onClick={handleDiscardConfirm} color="error">Discard</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
